import React, { useEffect, useState } from 'react';
import {
    ActionIcon,
    Badge,
    Flex,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconEye, IconSearch } from '@tabler/icons-react';
import { useElementSize } from '@mantine/hooks';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { useAlerts } from '../../../global/entities/Alert/queries';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { containsStringIgnoreCase } from './PatientTable.tsx';
import { AlertSeverity, AlertStatus, IClinicalAlert } from '../../../global/entities/Alert/Alert.ts';
import Utils from '../../../global/helpers/utils.ts';
import tableTools from '../../../global/helpers/tableTools.ts';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';

const severityColorMap: Record<string, string> = {
    [AlertSeverity.Low]: 'blue',
    [AlertSeverity.Moderate]: 'yellow',
    [AlertSeverity.High]: 'orange',
};

const statusColorMap: Record<string, string> = {
    [AlertStatus.Open]: 'red',
    [AlertStatus.Acknowledged]: 'teal',
    [AlertStatus.Escalated]: 'orange',
};

const AlertTable: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { ref, width } = useElementSize();
    const { sortedData, setSortedData, sortBy, reverseSortDirection, setSorting } = useTable();
    const [search, setSearch] = useState('');
    const { currentUser } = useSessionProvider();

    const { data: alerts, isLoading } = useAlerts({
        facilityId: currentUser?.selectedHealthcareFacilityID ?? undefined,
    });

    useEffect(() => {
        if (alerts) 
            setSortedData(tableTools.sortData([...alerts], { sortBy: 'severity', reversed: false, search: '' }));
        
    }, [alerts, setSortedData]);

    const getLocalizedTitle = (alert: IClinicalAlert): string => {
        return i18n.language === 'fr' ? alert.titleFr : alert.titleEn;
    };

    const getLocalizedDescription = (alert: IClinicalAlert): string | null => {
        return i18n.language === 'fr' ? alert.descriptionFr : alert.descriptionEn;
    };

    if (isLoading)
        return <Loading visible={true} loadingMessage={'Loading...'} />;

    return (
        <Paper ref={ref} h={'min-content'} w={'100%'}>
            <Stack h={'100%'}>
                <Flex align={'center'} gap={'sm'}>
                    <TextInput
                            hidden={width < 450}
                            placeholder={t('global.search')}
                            rightSection={width < 450 ? null : <IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            style={{ flex: 1 }}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                    </Flex>
                    <ScrollArea w="100%">
                        <Table verticalSpacing="xs" style={{ tableLayout: 'auto' }}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Th
                                        sorted={sortBy === 'triggeredDate'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('triggeredDate', sortedData)}
                                    >
                                        {t('alert.table.date')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'patientID'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('patientID', sortedData)}
                                    >
                                        {t('alert.table.patient')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'titleEn'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('titleEn', sortedData)}
                                    >
                                        {t('alert.table.description')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'severity'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('severity', sortedData)}
                                    >
                                        {t('alert.table.severity')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'status'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('status', sortedData)}
                                    >
                                        {t('alert.table.status')}
                                    </Th>
                                    <Table.Th style={{ width: 'min-content' }}></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {sortedData?.length === 0 && (
                                    <Table.Tr>
                                        <Table.Td colSpan={6}>
                                            <Text ta="center" c="dimmed" py="md">
                                                {t('alert.noAlerts')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                                {sortedData?.map((alert: IClinicalAlert, index: number) => {
                                    const title = getLocalizedTitle(alert);
                                    const description = getLocalizedDescription(alert);
                                    const baseText = description || title;
                                    const displayText = alert.triggerValue
                                        ? `${baseText} — ${t('alert.detail.triggerValue')}: ${alert.triggerValue}`
                                        : baseText;
                                    const fontWeight = alert.isVisited ? 400 : 700;
                                    const isSearch =
                                        containsStringIgnoreCase(
                                            [
                                                title,
                                                description || '',
                                                (alert as any).patientName || '',
                                                alert.severity,
                                                t(`alert.severity.${alert.severity.toLowerCase()}`),
                                                alert.status,
                                                t(`alert.status.${alert.status.toLowerCase()}`),
                                            ],
                                            search,
                                        ) || !search;
                                    return (
                                        <Table.Tr
                                            key={alert.id}
                                            hidden={!isSearch}
                                            style={{
                                                background:
                                                    index % 2 === 1
                                                        ? undefined
                                                        : 'var(--Background-Gray-light, #E9ECEF)',
                                            }}
                                        >
                                            <Table.Td>
                                                <Text fw={fontWeight} size={'sm'}>
                                                    {Utils.formatLocalDateToYYYYMMDD(alert.triggeredDate)}{' '}
                                                    {Utils.formatLocalDateToTime(alert.triggeredDate)}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td>
                                                <Text fw={fontWeight} size={'sm'}>
                                                    {(alert as any).patientName || `#${alert.patientID}`}
                                                </Text>
                                            </Table.Td>
                                            <Table.Td
                                                style={{
                                                    maxWidth: '300px',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                <Tooltip
                                                    label={displayText}
                                                    multiline
                                                    withArrow
                                                    position="top"
                                                    disabled={!displayText || displayText.length <= 40}
                                                    styles={{
                                                        tooltip: {
                                                            fontSize: '14px',
                                                            lineHeight: '1.4',
                                                            maxWidth: '300px',
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                        },
                                                    }}
                                                >
                                                    <Text
                                                        fw={fontWeight}
                                                        size={'sm'}
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {displayText}
                                                    </Text>
                                                </Tooltip>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={severityColorMap[alert.severity] || 'gray'}
                                                    variant="filled"
                                                    size="sm"
                                                >
                                                    {t(`alert.severity.${alert.severity.toLowerCase()}`)}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td>
                                                <Badge
                                                    color={statusColorMap[alert.status] || 'gray'}
                                                    variant="light"
                                                    size="sm"
                                                >
                                                    {t(`alert.status.${alert.status.toLowerCase()}`)}
                                                </Badge>
                                            </Table.Td>
                                            <Table.Td style={{ width: 'min-content' }}>
                                                <Tooltip label={t('alert.actions.view')}>
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="blue"
                                                        onClick={() => navigate(`../alert/${alert.id}`)}
                                                    >
                                                        <IconEye size={18} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                </ScrollArea>
            </Stack>
        </Paper>
    );
};

const WrappedAlertTable: React.FC = () => {
    return (
        <TableContextProvider>
            <AlertTable />
        </TableContextProvider>
    );
};

export default WrappedAlertTable;
